// AP Payment (settle posted invoices from a bank account) — mirrors Finance.API ApPaymentDto / ApPaymentInsert.
import { Currency, EntityKey } from '../ap-document/ap-document';

export enum ApPaymentStatus {
  Draft = 1,
  Posted = 2,
}

export enum ApPaymentType {
  Payment = 1,
}

export interface ApPaymentAllocation {
  documentReference: string;
  documentNumber: string;
  /** The provider's own printed/fiscal number (#4788), snapshotted at save so a posted payment still
   * renders it after its invoices have left the open payables. */
  fiscalNumber?: string;
  /** What this line discharged against the document, in `documentCurrency` (#5144) — the figure the user
   *  entered. `allocatedAmount` is derived from it. */
  settledAmount?: number;
  /** The document's currency, snapshotted at save (#5144). A posted payment's invoices leave the open
   *  payables, so re-reading it later is a lookup that may no longer resolve. */
  documentCurrency?: Currency;
  /** Document → payment multiplier used to derive the cash (#5144) — what it COST at the bank's quote.
   *  1 when the two currencies agree. */
  exchangeRate?: number;
  /** The invoice's own booking rate, document → functional (#5135) — what the payable was carried at, as
   *  against `exchangeRate` above. The gap between the two is the realized exchange gain or loss. */
  documentExchangeRate?: number;
  /** Cash allocated to this document (what the bank pays), in the PAYMENT's currency — derived from
   *  `settledAmount` less the advance; the rest of the settlement comes from an advance. */
  allocatedAmount: number;
  proratedFee?: number;
  /** Withholding for this invoice (#4304), proportional to allocatedAmount. Server-computed, read-only. */
  withholdingAmount?: number;
  /** Vendor Advance (Anticipo) drawn down against this document (#4349): settled = allocatedAmount + this. */
  advanceAppliedAmount?: number;
  /** The weekly Cash Request this line's money was planned under — a tag, like a cost centre. Null is
   * normal: paying outside a cash plan is legitimate. Carries the number so a list can name it. */
  cashRequest?: EntityKey | null;
}

/** Mirrors Global.Models S3ObjectDto (receipt attachments). */
export interface S3Object {
  name: string;
  presignedUrl: string;
}

/** Destination banking details frozen onto a payment or advance at post (#5143). Mirrors the anti-fraud
 *  snapshot the payment request has always kept: a provider's accounts change, and where a payment
 *  actually sent money is a fact about that payment. */
export interface ProviderBankingSnapshot {
  paymentBank?: {
    accountNumber?: string | null;
    swiftBicCode?: string | null;
    ibanCode?: string | null;
    bankName?: string | null;
    bankAddress?: string | null;
    currency?: number | null;
  } | null;
  intermediateBank?: unknown | null;
}

/**
 * What a payment exchanges at, for one currency (#5135) — units of `currency` per 1 USD, the way the bank
 * quotes it out loud ("twenty-four fifty to the dollar").
 *
 * One row per currency the payment actually has to exchange, and in practice that is one row: every
 * conversion routes through USD, so one side of any settleable pair IS USD and there is nothing to quote
 * on that side. Two rows appear only when a dollar payment settles both Lempira and Euro invoices.
 *
 * The quote rules the CASH. Each invoice is still relieved at its own booking rate, and the gap between
 * the two posts as a realized exchange gain or loss.
 */
export interface ApPaymentSettlementRate {
  currency: Currency;
  /** Units of `currency` per 1 USD — the figure shown and typed. */
  rateToUsd: number;
  /** The same quote as a currency → functional multiplier, derived server-side. */
  rate: number;
  /** True when the quote differs from the rate on file for the payment date — somebody typed it.
   *  Server-derived by comparison, never asserted by this client. */
  isManual: boolean;
}

export interface ApPayment {
  id?: string | null;
  number?: string;
  paymentType: ApPaymentType;
  paymentDate: string;
  fiscalYear?: number;
  fiscalPeriod?: number;
  providerReference: string;
  providerCode?: string;
  providerName?: string;
  financialCompanyReference?: string;
  amount: number;
  transferFee?: number;
  securityTax?: number;
  totalCashOut?: number;
  /** Total withholding across the allocations (#4304). Amount to send = amount − totalWithholding. */
  totalWithholding?: number;
  currency: Currency;
  /** What this payment exchanged at, per currency (#5135) — frozen when the draft was saved and spent
   *  unchanged at post. Empty when the payment converts nothing, which is most of them. */
  settlementRates?: ApPaymentSettlementRate[];
  bankAccountReference: string;
  /** Provider bank account the money was sent to — `providerDetails.banks[].id` (#5143). Optional: most
   *  providers have no accounts on file, and a payment without one is still valid. */
  selectedProviderBankId?: string | null;
  /** Destination banking details frozen at post (#5143) — what this payment actually paid into. */
  banking?: ProviderBankingSnapshot | null;
  bankGlAccountReference?: string | null;
  bankGlAccountNumber?: string | null;
  journalEntryReference?: string | null;
  journalEntryDocumentNumber?: string | null;
  bankTransactionReference?: string | null;
  bankTransactionNumber?: string | null;
  bankingReference?: string | null;
  attachments?: S3Object[];
  allocations: ApPaymentAllocation[];
  status: ApPaymentStatus;
  notes?: string | null;
  /** True when spawned (as a Draft) by approving an ApPaymentRequest. */
  autoCreated?: boolean;
  /** The ApPaymentRequest that spawned this payment (back-link; null for direct payments). */
  apPaymentRequestReference?: string | null;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

// Create-draft payload — mirrors ApPaymentInsert (Number/Status/GL/bank links derived server-side).
export interface ApPaymentAllocationInsert {
  documentReference: string;
  /**
   * What this line discharges against the document, in the DOCUMENT's currency (#5144) — including the part
   * covered by `advanceAppliedAmount`.
   *
   * The API derives the cash from it. Sending cash instead strands a residue on a foreign invoice: converting
   * a rounded dollar figure back to Lempiras lands fractionally short and leaves the document PartiallyPaid
   * over a cent nobody can pay.
   */
  settledAmount: number;
  /** Vendor Advance (Anticipo) to draw down against this document (#4349); the server resolves the FIFO
   * drawdown across the document's POs and caps it at what's available. Default 0. */
  advanceAppliedAmount?: number;
  /** Cash Request this line's money comes from — optional tag; the server resolves its number. */
  cashRequestReference?: string | null;
}

/** One quote on the way out: the server bounds it, defaults any currency it was not given to the day's
 *  rate, and drops any the payment does not actually exchange. */
export interface ApPaymentSettlementRateInsert {
  currency: Currency;
  rateToUsd: number;
}

export interface ApPaymentInsert {
  providerReference: string;
  /** API resolves the real value from the current site; the UI value is ignored. */
  financialCompanyReference: string;
  paymentDate: string;
  amount: number;
  transferFee?: number;
  securityTax?: number;
  currency: Currency;
  /** Optional on a draft (executing dept may select it later); required at post time. */
  bankAccountReference?: string | null;
  selectedProviderBankId?: string | null;
  notes?: string | null;
  /** What this payment exchanges at, per currency (#5135). The cash on every allocation is derived from
   *  these, so what is sent has to be what the screen showed when the amount was approved. */
  settlementRates?: ApPaymentSettlementRateInsert[];
  allocations: ApPaymentAllocationInsert[];
}

/** Body sent when posting: the real bank transaction number from the bank platform. */
export interface ApPaymentPostConfirm {
  bankTransactionNumber?: string | null;
}

export interface ApPaymentSearchCommand {
  financialCompanyReference?: string | null;
  providerReference?: string | null;
  number?: string | null;
  /** Matches an invoice the payment settles — our AP document number OR the provider's printed fiscal
   *  number, whichever the person searching happens to be holding. Both are snapshotted on every
   *  allocation at save, so drafts are as findable as posted payments. */
  invoiceNumber?: string | null;
  status?: ApPaymentStatus | null;
  /** Null on a draft awaiting an account, never null once posted. */
  bankAccountReference?: string | null;
  paymentDateFrom?: string | null;
  paymentDateTo?: string | null;
}
